import { useState } from "react";
import { Link } from "react-router-dom";
import { PlayerStats } from "../models/PlayerStats";
import PlayerImage from "./PlayerImage";
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/theme.css';
import styles from '../styles/Tables.module.css';
import { useDarkMode } from '../hooks/useDarkmode';

const PlayerTable = ({ players }: { players: PlayerStats[] }) => {
  const [sortProperty, setSortProperty] = useState<keyof PlayerStats>('rating2');

  const isDark = useDarkMode();

  const playerList = [...players].sort((a, b) => Number(b[sortProperty]) - Number(a[sortProperty]));

  const sortByProperty = (property: keyof PlayerStats) => {
    setSortProperty(property);
  }

  const getButtonLabel = (property: keyof PlayerStats) => {
    switch (property) {
      case 'gamesPlayed':
        return 'Kampe spillet';
      case 'kd':
        return 'K/D';
      case 'hs':
        return 'HS%';
      case 'adr':
        return 'ADR';
      case 'kast':
        return 'KAST%';
      case 'rating2':
        return 'Rating 2.0';
      default:
        return '';
    }
  }

  return (
    <div className="container">
      <div className="d-flex justify-content-center mb-3">
        <div className="dropdown">
          <button
            className="dropdown-toggle"
            type="button"
            id="dropdownMenuButton1"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            defaultValue={sortProperty}
            style={{
              backgroundColor: isDark ? 'var(--bg-secondary)' : '#f8f9fa',
              color: isDark ? '#ffffff' : '#212529',
              border: isDark ? '1px solid var(--border-primary)' : '1px solid #dee2e6',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease, border-color 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? 'var(--bg-tertiary)' : '#e9ecef';
              e.currentTarget.style.borderColor = 'var(--accent-orange)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? 'var(--bg-secondary)' : '#f8f9fa';
              e.currentTarget.style.borderColor = isDark ? 'var(--border-primary)' : '#dee2e6';
            }}
          >
            Sortér efter: {getButtonLabel(sortProperty)} 
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <path d="M6 9L1.5 4.5h9L6 9z"/>
            </svg>
          </button>
          <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
            <li><button className="dropdown-item" onClick={() => sortByProperty('gamesPlayed')}>Kampe spillet</button></li>
            <li><button className="dropdown-item" onClick={() => sortByProperty('kd')}>K/D</button></li>
            <li><button className="dropdown-item" onClick={() => sortByProperty('hs')}>HS%</button></li>
            <li><button className="dropdown-item" onClick={() => sortByProperty('adr')}>ADR</button></li>
            <li><button className="dropdown-item" onClick={() => sortByProperty('kast')}>KAST%</button></li>
            <li><button className="dropdown-item" onClick={() => sortByProperty('rating2')}>Rating 2.0</button></li>
          </ul>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr className={styles.tableHeader}>
              <th className={styles.tableHeaderCell} style={{ width: '5%' }}>#</th>
              <th className={styles.tableHeaderCell} style={{ width: '25%' }}>Spiller</th>
              <th className={styles.tableHeaderCell} style={{ width: '12%' }}>Kampe spillet</th>
              <th className={styles.tableHeaderCell} style={{ width: '10%' }}>K/D</th>
              <th className={styles.tableHeaderCell} style={{ width: '10%' }}>HS%</th>
              <th className={styles.tableHeaderCell} style={{ width: '10%' }}>ADR</th>
              <th className={styles.tableHeaderCell} style={{ width: '12%' }}>KAST%</th>
              <th className={styles.tableHeaderCell} style={{ width: '16%' }}>Rating 2.0</th>
            </tr>
          </thead>
          <tbody>
            {playerList.map((player, index) => (
              <tr key={player.steamID} className={`${styles.tableRow} ${styles.standard}`}>
                <td className={`align-middle text-center ${styles.tableCell}`}>
                  {index + 1}
                </td>
                <td className={`align-middle text-center ${styles.tableCell}`}>
                  <Link 
                    to={`/player/${player.steamID}`} 
                    className={`text-decoration-none text-light d-flex flex-column align-items-center`}
                  >
                    <div style={{ width: 50, height: 50 }}>
                      <PlayerImage id={player.steamID} width={50} />
                    </div>
                    <span className="mt-1 text-truncate" style={{ maxWidth: '100%' }}>
                      {player.account ? player.account.playerName : player.steamID}
                    </span>
                  </Link>
                </td>
                <td className={`align-middle text-center ${styles.tableCell}`}>
                  {player.gamesPlayed}
                </td>
                <td className={`align-middle text-center ${styles.tableCell}`}>
                  {(player.kd).toFixed(2)}
                </td>
                <td className={`align-middle text-center ${styles.tableCell}`}>
                  {(player.hs).toFixed(2)}%
                </td>
                <td className={`align-middle text-center ${styles.tableCell}`}>
                  {(player.adr).toFixed(2)}
                </td>
                <td className={`align-middle text-center ${styles.tableCell}`}>
                  {(player.kast).toFixed(2)}%
                </td>
                <td className={`align-middle text-center ${styles.tableCell} ${styles.bold}`}>
                  {(player.rating2).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PlayerTable;